'use client';

import type { ReactNode } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './dialog';
import { cn } from '@/lib/utils';

/**
 * Reusable dialog shell with two purpose-built chromes.
 *
 *  Mobile  (≤ 639px) → fullscreen panel matching the Dashboard Menu:
 *                      h-1 burgundy strip, h-12 header with text-xs uppercase
 *                      tracked title only, scrollable body, optional footer.
 *
 *  Desktop (≥ 640px) → centered modal with a rich header:
 *                      eyebrow (uppercase tracked, optional)
 *                      → large bold mixed-case title (with optional accent)
 *                      → muted description (optional)
 *                      → scrollable body, optional footer.
 *
 *  Consumers always pass `title`. `eyebrow`, `accent`, and `description` are
 *  desktop-only enrichments — when omitted, the desktop header still renders
 *  cleanly with just the title.
 */
export interface PanelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Used as the mobile thin-header label and the desktop large title. */
  title: string;
  /** Optional back-navigation control. When provided, a compact back arrow
   *  renders before the title in every header variant (mobile, compact,
   *  rich) — e.g. closing this dialog to return to a parent list/board. */
  onBack?: () => void;
  /** Desktop only — uppercase tracked label above the title. */
  eyebrow?: string;
  /** Desktop only — highlighted accent block rendered after the title. */
  accent?: ReactNode;
  /** Desktop only — muted description below the title. */
  description?: string;
  /** Desktop only header chrome. `compact` uses a smaller minimal title row. */
  desktopHeaderVariant?: 'rich' | 'compact';
  /** Rendered in the sticky footer bar below the scrollable body. */
  footer?: ReactNode;
  /** Tailwind class to override desktop max-width. Defaults to sm:max-w-3xl. */
  desktopMaxWidth?: string;
  /** Tailwind class to override desktop fixed height. Defaults to sm:h-[min(88vh,_760px)]. */
  desktopHeight?: string;
  /** Extra classes for the scrollable body — e.g. override the default `bg-card`. */
  bodyClassName?: string;
  children: ReactNode;
}

/**
 * Compact back-navigation control shared by every header variant. Rendered
 * as a real, focusable control (never inside an `aria-hidden` wrapper) so it
 * stays reachable by keyboard and announced by AT even though the header
 * text around it is decorative.
 */
function HeaderBackButton({
  onBack,
  className,
}: {
  onBack: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onBack}
      aria-label="Back"
      className={cn(
        'inline-flex h-6 w-6 flex-none items-center justify-center rounded-sm',
        'text-muted-foreground transition-colors duration-[140ms] hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-0',
        className,
      )}
    >
      <ArrowLeft className="h-4 w-4" />
    </button>
  );
}

export function PanelDialog({
  open,
  onOpenChange,
  title,
  onBack,
  eyebrow,
  accent,
  description,
  desktopHeaderVariant = 'rich',
  footer,
  desktopMaxWidth = 'sm:max-w-3xl',
  desktopHeight = 'sm:h-[min(88vh,_760px)]',
  bodyClassName,
  children,
}: PanelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        // When there's no description, opt out of Radix's aria-describedby so it
        // doesn't warn about a missing DialogDescription; when there is one, the
        // sr-only <DialogDescription> below wires it up automatically.
        {...(description ? {} : { 'aria-describedby': undefined })}
        className={cn(
          // Mobile: fullscreen panel
          'left-0 top-0 flex h-dvh w-screen max-w-none translate-x-0 translate-y-0',
          'flex-col gap-0 overflow-hidden rounded-none border-0 p-0',
          // Desktop: centered modal at a fixed height — prevents the dialog
          // from shrinking when body content is loading and growing once the
          // list arrives. Stable frame = stable burgundy accent perception.
          'sm:left-[50%] sm:top-[50%] sm:-translate-x-1/2 sm:-translate-y-1/2',
          'sm:w-[calc(100vw-2rem)]',
          'sm:rounded-none sm:border sm:border-border',
          desktopMaxWidth,
          desktopHeight,
        )}
        // Inline style guarantees no top border can leak through (the dialog
        // primitive bakes in `border-t border-t-primary` and Tailwind
        // class-merge ordering has been unreliable here). Burgundy strip
        // below is the only top accent.
        style={{
          boxShadow: 'var(--shadow-warm-md)',
          borderTopWidth: 0,
        }}
      >
        {/* The single semantic name + heading for the dialog. The visible title
            treatments below are decorative duplicates (aria-hidden), so this is
            the only accessible name and the only heading exposed to AT. sr-only
            (clip, not display:none) keeps it exposed at every breakpoint. */}
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {description ? (
          <DialogDescription className="sr-only">{description}</DialogDescription>
        ) : null}

        <div className="relative flex h-full min-h-0 flex-col bg-background">
          {/* Burgundy accent strip — same on both breakpoints. shrink-0 keeps
              it at exactly 2px regardless of flex pressure. */}
          <div className="h-0.5 w-full shrink-0 bg-[var(--burgundy-core)]" />

          {/* MOBILE HEADER — thin uppercase label. The label stays decorative
              (aria-hidden — the accessible name comes from the sr-only
              DialogTitle above), but the optional back button is a real
              control, so it sits outside that hidden text. */}
          <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4 pr-12 sm:hidden">
            {onBack ? <HeaderBackButton onBack={onBack} /> : null}
            <p
              aria-hidden="true"
              className="text-xs font-bold uppercase tracking-[0.18em] text-foreground"
            >
              {title}
            </p>
          </div>

          {/* DESKTOP HEADER — rich or compact variant. The eyebrow/title/
              description text stays decorative (aria-hidden on each text
              node — the accessible name comes from the sr-only DialogTitle
              above, so no <h2> here reaches the a11y tree). The optional
              back button is a real control and sits outside that hidden
              text, in the same row, so it stays focusable and announced.

              Both variants keep the title on the FIRST line of the text stack
              at a fixed top padding so the absolutely-positioned X (below) can
              anchor to the title's top line — never vertically centered against
              a taller title+description block. */}
          {desktopHeaderVariant === 'compact' ? (
            <div className="hidden shrink-0 border-b border-border px-7 py-2.5 pr-16 sm:flex sm:flex-row sm:items-center sm:gap-2">
              {onBack ? <HeaderBackButton onBack={onBack} /> : null}
              <div className="flex min-w-0 flex-col gap-1">
                <p aria-hidden="true" className="text-overline text-foreground">
                  {title}
                </p>
                {description ? (
                  <p
                    aria-hidden="true"
                    className="max-w-2xl text-caption text-muted-foreground"
                  >
                    {description}
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="hidden shrink-0 border-b border-border px-7 pt-5 pb-4 pr-16 sm:flex sm:flex-row sm:items-start sm:gap-2">
              {onBack ? (
                <HeaderBackButton onBack={onBack} className="mt-0.5" />
              ) : null}
              <div className="flex min-w-0 flex-col gap-2">
                {eyebrow ? (
                  <span
                    aria-hidden="true"
                    className="text-xs font-semibold uppercase tracking-[0.32em] text-muted-foreground"
                  >
                    {eyebrow}
                  </span>
                ) : null}
                <p aria-hidden="true" className="text-title-lg text-foreground">
                  <span className="block">{title}</span>
                  {accent ? (
                    <span
                      className={cn(
                        'mt-2 inline-block max-w-full break-words align-middle',
                        'border-l-[3px] border-[var(--burgundy-core)]',
                        'bg-gradient-to-r from-primary/20 to-primary/10',
                        'px-2.5 py-0.5 text-[0.86em] font-extrabold leading-tight',
                      )}
                    >
                      {accent}
                    </span>
                  ) : null}
                </p>
                {description ? (
                  <p
                    aria-hidden="true"
                    className="max-w-2xl text-body leading-snug text-muted-foreground"
                  >
                    {description}
                  </p>
                ) : null}
              </div>
            </div>
          )}

          {/* X button — anchored top-right, aligned to the title's first
              line. A fixed `top` per breakpoint/variant keeps the X on that
              line regardless of whether a description is present below it,
              so it never drifts downward with taller header content. */}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className={cn(
              'absolute inline-flex h-8 w-8 items-center justify-center',
              'border border-transparent bg-transparent text-muted-foreground',
              'transition-all duration-[140ms]',
              'hover:bg-muted/40 hover:text-foreground',
              'focus-visible:outline-none focus-visible:border-primary',
              'focus-visible:ring-[3px] focus-visible:ring-primary/10 focus-visible:ring-offset-0',
              // Mobile: centered in the h-12 header (burgundy h-0.5 + (48-32)/2)
              'right-2 top-2.5',
              // Desktop: right inset shared. Compact's header is a single
              // text-overline line (12px line-height) after the 2px strip +
              // 12px top padding, so top-1 (4px) truly centers the 32px
              // button on it: 2 + 12 + 12/2 - 32/2 = 4. Rich anchors to the
              // title's top line instead of centering on the full (often
              // taller) eyebrow+title+description block, so it keeps its
              // own top padding as the offset.
              'sm:right-4',
              desktopHeaderVariant === 'compact' ? 'sm:top-1.5' : 'sm:top-5',
            )}
          >
            <X className="h-4 w-4" />
          </button>

          {/* Body */}
          <div
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-contain bg-card',
              'px-4 pb-6 pt-4',
              'sm:px-7 sm:pt-6 sm:pb-8',
              bodyClassName,
            )}
          >
            {children}
          </div>

          {/* Footer — shrink-0 so it stays anchored at the bottom regardless of body height. */}
          {footer ? (
            <div
              className={cn(
                'shrink-0 border-t border-border bg-card',
                'px-4 py-4',
                'sm:px-7 sm:py-5',
              )}
            >
              {footer}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
